import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Link } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';

import type { ImageItem } from 'hb-builder/components/BuilderComponents/block/Strips/types/items';
import LoadingBox from 'hb-builder/components/common/LoadingComponents/LoadingBox';
import { getIsExternalLink, getLink } from 'hb-builder/utils/links';

interface ImageProps {
  clickable?: boolean;
  hasFrameBackgroundColor: boolean;
  fallBackAltValue?: string;
  image?: ImageItem;
  subdomainNetworkUrl?: string;
}

const Image = ({
  clickable,
  hasFrameBackgroundColor,
  fallBackAltValue,
  image,
  subdomainNetworkUrl,
}: ImageProps) => {
  if (!image) {
    return null;
  }

  const {
    _imageData,
    alt: imageAlt,
    isLoadingSrc,
    url: imageUrl,
    position: imagePosition,
  } = image.attributes || {};

  const fullImageUrl =
    imageUrl?.startsWith('/images/missing') && subdomainNetworkUrl
      ? `${subdomainNetworkUrl}${imageUrl}`
      : imageUrl;

  if (!fullImageUrl) {
    return null;
  }

  const alt = imageAlt || fallBackAltValue;

  return (
    <Wrapper
      $hasFrameBackgroundColor={hasFrameBackgroundColor}
      dataTestId="dumb-square-image"
    >
      <StyledImage
        $clickable={clickable}
        $image={_imageData || fullImageUrl}
        $position={imagePosition}
      >
        {isLoadingSrc && (
          <LoadingBox>
            <Loader size={40} />
          </LoadingBox>
        )}
        {alt && <span role="img" aria-label={alt} />}
      </StyledImage>
    </Wrapper>
  );
};

interface DumbSquaredImageProps extends ImageProps {
  clickable?: boolean;
  fallBackAltValue?: string;
  isBackoffice?: boolean;
  link?: string;
  linkNewPage?: boolean;
}

const DumbSquaredImage = ({
  clickable,
  hasFrameBackgroundColor,
  fallBackAltValue,
  image,
  isBackoffice,
  link,
  linkNewPage,
  subdomainNetworkUrl,
}: DumbSquaredImageProps) => {
  const imageProps = {
    clickable,
    fallBackAltValue,
    hasFrameBackgroundColor,
    image,
    subdomainNetworkUrl,
  };

  const imageIsClickable = link && clickable;

  if ((isBackoffice && clickable) || !imageIsClickable) {
    return <Image {...imageProps} />;
  }

  const domain = window.location.hostname;

  const linkProps = {
    forceDefaultLink: getIsExternalLink(link, domain),
    target: linkNewPage ? '_blank' : '_self',
    to: !isBackoffice ? getLink(link, domain) : '#',
  };

  return (
    <Link {...linkProps} style={{ width: '100%' }} rel="noreferrer">
      <Image {...imageProps} />
    </Link>
  );
};

export default DumbSquaredImage;

interface StyledImageProps {
  $clickable?: boolean;
  $image: string;
  $position?: string;
}

const StyledImage = styled(Box).attrs(() => ({
  height: 0,
  pb: '100%',
  width: '100%',
}))<StyledImageProps>`
  background-image: ${({ $image }) => `url(${$image})`};
  background-position: ${({ $position }) => $position};
  background-repeat: no-repeat;
  background-size: cover;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  object-fit: cover;
  position: relative;
  transition: transform 0.6s;
  &:hover {
    transform: scale(1.1) !important;
  }
`;

interface WrapperProps {
  $hasFrameBackgroundColor: ImageProps['hasFrameBackgroundColor'];
}

const Wrapper = styled(Box).attrs({
  flexShrink: 0,
  mt: { _: 6, l: 0 }, // slider component adds margin top on items only from L breakpoint
  overflow: 'hidden',
})<WrapperProps>`
  ${({ $hasFrameBackgroundColor, theme }) =>
    css`
      border-radius: ${$hasFrameBackgroundColor
        ? `${theme.themeVariables.globalBorderRadius}px ${theme.themeVariables.globalBorderRadius}px 0px 0px`
        : `${theme.themeVariables.globalBorderRadius}px`};
    `}
`;
